import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

function TopStocksTable({ stocks, totalStocks }) {
  const { t } = useTranslation();
  const formatMarketCap = (value) => {
    if (!value) return '-';
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)} T`;
    } else if (value >= 1) {
      return `${value.toFixed(2)} B`;
    } else {
      return `${(value * 1000).toFixed(2)} M`;
    }
  };

  if (!stocks || stocks.length === 0) return null;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0.5,
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('sectorsManagement.details.topStocks.title')}
            </Typography>
          </Box>
          <Chip
            label={`${totalStocks} ${t('sectorsManagement.details.topStocks.stocks')}`}
            color="primary"
            size="small"
          />
        </Box>
        <TableContainer 
          sx={{ 
            overflowX: 'auto', 
            width: '100%',
            mt: 0,
          }}
        >
          <Table 
            size="medium" 
            sx={{ 
              width: '100%',
              '& .MuiTableCell-root': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                padding: 0,
                px: 2,
                py: 1.5,
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none',
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                  {t('sectorsManagement.details.topStocks.ticker')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                  {t('sectorsManagement.details.topStocks.companyName')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                  {t('sectorsManagement.details.topStocks.marketCap')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.map((stock, idx) => (
                <TableRow
                  key={stock.ticker}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#667eea', 0.05),
                      transform: 'translateX(2px)',
                    },
                    bgcolor: idx % 2 === 0 ? 'white' : alpha('#667eea', 0.01),
                    borderBottom: idx === stocks.length - 1 ? 'none' : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <TableCell sx={{ width: 60 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        color: idx < 3
                          ? '#374151'
                          : '#64748b',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {idx + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#3182ce',
                        fontSize: '0.95rem',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {stock.ticker}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    <Typography 
                      variant="body2"
                      sx={{
                        color: '#2d3748',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.5,
                      }}
                      title={stock.company_name}
                    >
                      {stock.company_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {stock.market_cap_bn ? (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#1e293b',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        {formatMarketCap(stock.market_cap_bn)}
                      </Typography>
                    ) : (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default TopStocksTable;

